import { ApolloLink, execute, type FetchResult, Observable, type Operation } from '@apollo/client';
import {
  type DefinitionNode,
  type DocumentNode,
  type FieldNode,
  type FragmentDefinitionNode,
  Kind,
  type OperationDefinitionNode,
  type VariableDefinitionNode,
  visit,
} from 'graphql';
import type { GraphQLFormattedError } from 'graphql/error';

// O gateway recusa batch HTTP ("Operation batching disabled"), então a chamada única é um documento só:
// cada query vira um grupo de campos com alias e variáveis prefixados, e a resposta é repartida depois.
const MERGED_OPERATION_NAME = 'MergedQueries';
// Espera um pouco por queries que saem no mesmo render; o teto evita segurar a primeira por muito tempo.
const DEBOUNCE_MS = 30;
const MAX_WAIT_MS = 250;

let activeScopes = 0;

/** Liga o merge enquanto houver um escopo ativo (ex.: a home montada). Devolve a função que desliga. */
export const acquireQueryMergeScope = () => {
  activeScopes += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    activeScopes = Math.max(0, activeScopes - 1);
  };
};

// Mocks do E2E (Playwright marca `navigator.webdriver`) e do demo (MSW) respondem por operationName;
// uma operação combinada não casaria com nenhum deles.
const isMergeDisabledEnvironment = () => process.env.REACT_APP_DEMO_MODE === 'true' || (typeof navigator !== 'undefined' && navigator.webdriver === true);

export const isQueryMergeActive = () => activeScopes > 0 && !isMergeDisabledEnvironment();

interface ResultObserver {
  next: (result: FetchResult) => void;
  error: (error: unknown) => void;
  complete: () => void;
}

interface PendingOperation {
  operation: Operation;
  observer: ResultObserver;
  cancelled: boolean;
}

const prefixFor = (index: number) => `m${index}_`;

const getOperationDefinition = (query: DocumentNode) =>
  query.definitions.find((definition): definition is OperationDefinitionNode => definition.kind === Kind.OPERATION_DEFINITION);

/** Só queries simples entram: campos na raiz, sem diretivas de operação nem fragmentos na raiz. */
const isMergeable = (operation: Operation) => {
  const definitions = operation.query.definitions.filter((definition) => definition.kind === Kind.OPERATION_DEFINITION);
  const definition = getOperationDefinition(operation.query);
  if (definitions.length !== 1 || !definition || definition.operation !== 'query') return false;
  if (definition.directives?.length) return false;
  return definition.selectionSet.selections.every((selection) => selection.kind === Kind.FIELD);
};

const rootKeys = (definition: OperationDefinitionNode) => (definition.selectionSet.selections as FieldNode[]).map((field) => field.alias?.value ?? field.name.value);

const prefixDocument = (query: DocumentNode, prefix: string) =>
  visit(query, {
    Variable: (node) => ({ ...node, name: { ...node.name, value: prefix + node.name.value } }),
    FragmentSpread: (node) => ({ ...node, name: { ...node.name, value: prefix + node.name.value } }),
    FragmentDefinition: (node) => ({ ...node, name: { ...node.name, value: prefix + node.name.value } }),
  });

export const buildMergedQuery = (operations: Pick<Operation, 'query' | 'variables'>[]) => {
  const variableDefinitions: VariableDefinitionNode[] = [];
  const selections: FieldNode[] = [];
  const fragments: FragmentDefinitionNode[] = [];
  const variables: Record<string, unknown> = {};

  operations.forEach((operation, index) => {
    const prefix = prefixFor(index);
    const document = prefixDocument(operation.query, prefix);

    for (const definition of document.definitions as DefinitionNode[]) {
      if (definition.kind === Kind.FRAGMENT_DEFINITION) {
        fragments.push(definition);
      } else if (definition.kind === Kind.OPERATION_DEFINITION) {
        variableDefinitions.push(...(definition.variableDefinitions ?? []));
        for (const field of definition.selectionSet.selections as FieldNode[]) {
          const key = field.alias?.value ?? field.name.value;
          selections.push({ ...field, alias: { kind: Kind.NAME, value: prefix + key } });
        }
      }
    }

    for (const [name, value] of Object.entries(operation.variables ?? {})) {
      variables[prefix + name] = value;
    }
  });

  const mergedOperation: OperationDefinitionNode = {
    kind: Kind.OPERATION_DEFINITION,
    operation: 'query' as OperationDefinitionNode['operation'],
    name: { kind: Kind.NAME, value: MERGED_OPERATION_NAME },
    variableDefinitions,
    directives: [],
    selectionSet: { kind: Kind.SELECTION_SET, selections },
  };

  const query: DocumentNode = { kind: Kind.DOCUMENT, definitions: [mergedOperation, ...fragments] };
  return { query, variables };
};

export const splitMergedResult = (result: FetchResult, operations: Pick<Operation, 'query'>[]): FetchResult[] =>
  operations.map((operation, index) => {
    const prefix = prefixFor(index);
    const definition = getOperationDefinition(operation.query) as OperationDefinitionNode;
    const data = result.data ? Object.fromEntries(rootKeys(definition).map((key) => [key, (result.data as Record<string, unknown>)[prefix + key] ?? null])) : result.data;

    const errors = (result.errors as readonly GraphQLFormattedError[] | undefined)
      ?.filter((error) => !error.path?.length || String(error.path[0]).startsWith(prefix))
      .map((error) => (error.path?.length ? { ...error, path: [String(error.path[0]).slice(prefix.length), ...error.path.slice(1)] } : error));

    return { ...result, data, ...(errors?.length ? { errors } : { errors: undefined }) } as FetchResult;
  });

/**
 * Link terminal que, com um escopo de merge ativo, junta as queries disparadas juntas numa única
 * requisição. Se a chamada combinada falhar inteira (rede ou `data` nulo), cada query é refeita
 * sozinha, para uma query quebrada não derrubar as outras.
 */
export const createMergeQueriesLink = (terminatingLink: ApolloLink) => {
  let pending: PendingOperation[] = [];
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  let maxWaitTimer: ReturnType<typeof setTimeout> | undefined;

  // Operação original, com o contexto (headers) que o authLink já montou.
  const forwardAlone = (operation: Operation) => terminatingLink.request(operation) ?? Observable.of<FetchResult>();

  const runAlone = (entry: PendingOperation) => forwardAlone(entry.operation).subscribe(entry.observer);

  const flush = () => {
    clearTimeout(debounceTimer);
    clearTimeout(maxWaitTimer);
    debounceTimer = undefined;
    maxWaitTimer = undefined;

    const batch = pending.filter((entry) => !entry.cancelled);
    pending = [];
    if (batch.length === 0) return;
    if (batch.length === 1) {
      runAlone(batch[0]);
      return;
    }

    const operations = batch.map((entry) => entry.operation);
    const { query, variables } = buildMergedQuery(operations);

    execute(terminatingLink, { query, variables, operationName: MERGED_OPERATION_NAME, context: operations[0].getContext() }).subscribe({
      next: (result) => {
        if (!result.data) {
          for (const entry of batch) if (!entry.cancelled) runAlone(entry);
          return;
        }
        const results = splitMergedResult(result, operations);
        batch.forEach((entry, index) => {
          if (entry.cancelled) return;
          entry.observer.next(results[index]);
          entry.observer.complete();
        });
      },
      error: () => {
        for (const entry of batch) if (!entry.cancelled) runAlone(entry);
      },
    });
  };

  return new ApolloLink((operation) => {
    if (!(isQueryMergeActive() && isMergeable(operation))) return forwardAlone(operation);

    return new Observable<FetchResult>((observer) => {
      const entry: PendingOperation = { operation, observer, cancelled: false };
      pending.push(entry);

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(flush, DEBOUNCE_MS);
      maxWaitTimer ??= setTimeout(flush, MAX_WAIT_MS);

      return () => {
        entry.cancelled = true;
      };
    });
  });
};
